import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  deleteInquiryDetails,
  getAllInquiryDetails,
} from "../../api/InquiryDetails";
import "../../vendor/css/viewinquirydetails.css";

const ViewInquiryDetails = () => {
  const [inquiryDetailsList, setInquiryDetailsList] = useState([]);
  const [selectedInquiryDetails, setSelectedInquiryDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const inquiriesPerPage = 10;

  useEffect(() => {
    const getInquiryDetailsList = async () => {
      try {
        const response = await getAllInquiryDetails();
        setInquiryDetailsList(response?.inquiryDetails || response || []);
      } catch (error) {
        console.error("Error fetching inquiry details:", error);
        Swal.fire("Error", "Failed to load inquiry details", "error");
      } finally {
        setLoading(false);
      }
    };
    getInquiryDetailsList();
    const interval = setInterval(getInquiryDetailsList, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleDeleteInquiryDetails = (InquiryDetailsToDelete) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteInquiryDetails(InquiryDetailsToDelete)
          .then((res) => {
            if (res.status === 200) {
              Swal.fire("Deleted!", "Your file has been deleted.", "success");
              setInquiryDetailsList((prev) =>
                prev.filter((item) => item._id !== InquiryDetailsToDelete._id)
              );
            }
          })
          .catch((err) => {
            console.error("Delete failed:", err);
            Swal.fire("Error", "Failed to delete inquiry", "error");
          });
      }
    });
  };

  // Filter inquiries based on search term
  const filteredInquiries = inquiryDetailsList.filter(inquiry =>
    inquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inquiry.phone_no?.includes(searchTerm) ||
    inquiry.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastInquiry = currentPage * inquiriesPerPage;
  const indexOfFirstInquiry = indexOfLastInquiry - inquiriesPerPage;
  const currentInquiries = filteredInquiries.slice(indexOfFirstInquiry, indexOfLastInquiry);
  const totalPages = Math.ceil(filteredInquiries.length / inquiriesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="view-inquiries">
        <div className="inquiries-header">
          <h1>Inquiry Management</h1>
          <p>View and manage all customer inquiries</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading inquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-inquiries">
      <div className="inquiries-header">
        <h1 style={{ fontFamily: 'serif' }}>Inquiry Management</h1>
        <p style={{ fontFamily: 'serif' }}>View and manage all customer inquiries</p>
      </div>

      <div className="inquiries-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-message"></i>
          </div>
          <div className="stat-content">
            <h3>{inquiryDetailsList.length}</h3>
            <p>Total Inquiries</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-search"></i>
          </div>
          <div className="stat-content">
            <h3>{filteredInquiries.length}</h3>
            <p>Filtered Results</p>
          </div>
        </div>
      </div>

      <div className="inquiries-controls">
        <div className="search-container">
          <i className="bx bx-search search-icon"></i>
          <input
            type="text"
            placeholder="Search by name, email, phone, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="inquiries-table-container">
        <table className="inquiries-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact Information</th>
              <th>Message</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentInquiries.length > 0 ? (
              currentInquiries.map((inquiry, index) => (
                <tr key={inquiry._id || index}>
                  <td>
                    <div className="inquirer-name">
                      <div className="avatar">
                        {inquiry.name?.charAt(0).toUpperCase()}
                      </div>
                      <span>{inquiry.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <a href={`tel:${inquiry.phone_no}`} className="contact-link">
                        <i className="bx bx-phone"></i>
                        {inquiry.phone_no}
                      </a>
                      <a href={`mailto:${inquiry.email}`} className="contact-link">
                        <i className="bx bx-envelope"></i>
                        {inquiry.email}
                      </a>
                    </div>
                  </td>
                  <td>
                    <div className="message-content">
                      <span className="message-text">
                        {inquiry.message?.length > 100
                          ? `${inquiry.message.substring(0, 100)}...`
                          : inquiry.message
                        }
                      </span>
                      {inquiry.message?.length > 100 && (
                        <button
                          className="read-more-btn"
                          onClick={() => Swal.fire({
                            title: 'Full Message',
                            text: inquiry.message,
                            confirmButtonText: 'Close'
                          })}
                        >
                          Read More
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-danger delete-btn"
                        onClick={() => handleDeleteInquiryDetails(inquiry)}
                        title="Delete Inquiry"
                      >
                        <i className="bx bx-trash"></i>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="no-data">
                  <div className="no-data-content">
                    <i className="bx bx-message-x"></i>
                    <p>No inquiries found</p>
                    <small>Try adjusting your search criteria</small>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-left"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewInquiryDetails;
